import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const TeacherProfile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState({
    name: user?.name || 'Jane Smith',
    email: user?.email || 'teacher@example.com',
    employeeId: 'EMP001',
    designation: 'Professor',
    department: 'Computer Science',
    qualification: 'PhD in Computer Science',
    experience: 10,
    salary: 75000,
    joiningDate: '2013-08-15',
    phone: '+1234567890',
    address: '123 Education Street, City, Country'
  });

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(profileData);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await api.get(`/teachers/user/${user?._id}`);
        const teacherData = response.data.data;
        
        const profileInfo = {
          name: teacherData.userId?.name || user?.name || 'Jane Smith',
          email: teacherData.userId?.email || user?.email || 'teacher@example.com',
          employeeId: teacherData.employeeId || 'EMP001',
          designation: teacherData.designation || 'Professor',
          department: teacherData.department || 'Computer Science',
          qualification: teacherData.qualification || 'PhD in Computer Science',
          experience: teacherData.experience || 10,
          salary: teacherData.salary || 75000,
          joiningDate: teacherData.joiningDate || '2013-08-15',
          phone: teacherData.phone || '+1234567890',
          address: teacherData.address || '123 Education Street, City, Country'
        };
        
        setProfileData(profileInfo);
        setFormData(profileInfo);
      } catch (error) {
        console.error('Error fetching profile data:', error);
        // Fallback to default data if API fails
        setFormData(profileData);
      }
    };

    if (user) {
      fetchProfileData();
    }
  }, [user]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEditToggle = async () => {
    if (editMode) {
      // Save changes to backend
      try {
        await api.put(`/teachers/user/${user?._id}`, {
          ...formData,
          userId: user._id
        });
        setProfileData(formData);
        alert('Profile updated successfully!');
      } catch (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    }
    setEditMode(!editMode);
  };

  const handleCancel = () => {
    setFormData(profileData);
    setEditMode(false);
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">My Profile</h1>
        
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Teacher Information</h2>
            <button
              onClick={handleEditToggle}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            >
              {editMode ? 'Save Changes' : 'Edit Profile'}
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">Full Name</label>
              {editMode ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-800">{profileData.name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Email</label>
              {editMode ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-800">{profileData.email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Employee ID</label>
              <p className="text-gray-800">{profileData.employeeId}</p>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Designation</label>
              {editMode ? (
                <select
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="Professor">Professor</option>
                  <option value="Assistant Professor">Assistant Professor</option>
                  <option value="Lecturer">Lecturer</option>
                  <option value="Instructor">Instructor</option>
                  <option value="Head of Department">Head of Department</option>
                </select>
              ) : (
                <p className="text-gray-800">{profileData.designation}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Department</label>
              {editMode ? (
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="Computer Science">Computer Science</option>
                  <option value="Mathematics">Mathematics</option>
                  <option value="Physics">Physics</option>
                  <option value="Chemistry">Chemistry</option>
                  <option value="Biology">Biology</option>
                  <option value="English">English</option>
                  <option value="History">History</option>
                  <option value="Geography">Geography</option>
                  <option value="Economics">Economics</option>
                  <option value="Business Studies">Business Studies</option>
                </select>
              ) : (
                <p className="text-gray-800">{profileData.department}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Qualification</label>
              {editMode ? (
                <input
                  type="text"
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  placeholder="e.g. PhD in Computer Science"
                />
              ) : (
                <p className="text-gray-800">{profileData.qualification}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Experience (Years)</label>
              {editMode ? (
                <input
                  type="number"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full p-2 border rounded"
                  placeholder="Enter years of experience"
                />
              ) : (
                <p className="text-gray-800">{profileData.experience} years</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Salary</label>
              {editMode ? (
                <input
                  type="number"
                  name="salary"
                  value={formData.salary}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full p-2 border rounded"
                  placeholder="Enter salary amount"
                />
              ) : (
                <p className="text-gray-800">${profileData.salary.toLocaleString()}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Joining Date</label>
              {editMode ? (
                <input
                  type="date"
                  name="joiningDate"
                  value={formData.joiningDate}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                />
              ) : (
                <p className="text-gray-800">{profileData.joiningDate}</p>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Phone</label>
              {editMode ? (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  placeholder="Enter phone number"
                />
              ) : (
                <p className="text-gray-800">{profileData.phone}</p>
              )}
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-gray-700 mb-2">Address</label>
            {editMode ? (
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full p-2 border rounded"
                placeholder="Enter address"
              ></textarea>
            ) : (
              <p className="text-gray-800">{profileData.address}</p>
            )}
          </div>
          
          {editMode && (
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={handleCancel}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherProfile;